import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["botao", "status", "progresso", "barra", "contadores", "erro", "falhas", "legado"]
  static values = { iniciarUrl: String, progressoUrl: String }

  connect() {
    this.desconectado = false
    this.bloqueado = this.hasBotaoTarget && this.botaoTarget.disabled
    this.consultar()
  }

  disconnect() {
    this.desconectado = true
    clearTimeout(this.timer)
    this.requisicao?.abort()
  }

  async iniciar() {
    if (this.iniciando) return
    this.iniciando = true
    clearTimeout(this.timer)
    this.requisicao?.abort()
    this.botaoTarget.disabled = true
    this.limparErro()
    try {
      const dados = await this.buscar(this.iniciarUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
      })
      this.atualizar(dados)
    } catch (erro) {
      if (!this.desconectado) {
        this.mostrarErro(erro.message)
        this.botaoTarget.disabled = this.bloqueado
        this.agendar()
      }
    } finally {
      this.iniciando = false
    }
  }

  async consultar() {
    try {
      const dados = await this.buscar(this.progressoUrlValue)
      this.limparErro()
      this.atualizar(dados)
    } catch (erro) {
      if (erro.name === "AbortError" || this.desconectado) return
      this.mostrarErro(erro.message)
      if (this.statusTarget.textContent.includes("Carregando")) this.statusTarget.textContent = "Não foi possível consultar a importação."
      this.agendar()
    }
  }

  async buscar(url, opcoes = {}) {
    this.requisicao = new AbortController()
    const resposta = await fetch(url, {
      ...opcoes, signal: this.requisicao.signal,
      headers: { Accept: "application/json", ...opcoes.headers }
    })
    if (!resposta.headers.get("content-type")?.includes("application/json")) {
      throw new Error("Não foi possível consultar a importação. Verifique sua sessão.")
    }
    const dados = await resposta.json()
    if (!resposta.ok) throw new Error(dados.error || "Não foi possível consultar a importação.")
    return dados
  }

  atualizar(dados) {
    if (this.desconectado) return
    const estados = {
      nao_iniciada: "Nenhuma importação realizada.", aguardando: "Aguardando processamento…",
      executando: "Importando acessos…", concluida: "Importação concluída.",
      concluida_com_erros: "Importação concluída com pendências. Você pode importar novamente.",
      falhou: "Importação interrompida. Você pode tentar novamente."
    }
    const percentual = Math.max(0, Math.min(100, Number(dados.progress) || 0))
    this.statusTarget.textContent = estados[dados.status] || "Consultando importação…"
    if (Object.hasOwn(dados, "legado_importado_em")) {
      this.legadoTarget.textContent = dados.legado_importado_em ?
        `Importação registrada no histórico do jogo em ${new Date(dados.legado_importado_em).toLocaleString("pt-BR", { timeZone: "America/Fortaleza" })}.` :
        "Sem data de importação registrada no histórico do jogo."
    }
    this.progressoTarget.setAttribute("aria-valuenow", percentual)
    this.barraTarget.style.width = `${percentual}%`
    this.barraTarget.textContent = `${percentual}%`
    this.barraTarget.classList.toggle("progress-bar-striped", dados.ativa)
    this.barraTarget.classList.toggle("progress-bar-animated", dados.ativa)
    this.contadoresTarget.textContent = dados.id ?
      `${dados.processados} de ${dados.total} check-ins processados · ${dados.confirmados} acessos confirmados · ${dados.sem_acesso} sem acesso identificado · ${dados.ignorados} ignorados · ${dados.erros} erros` : ""
    this.falhasTarget.replaceChildren()
    for (const falha of dados.falhas || []) {
      const linha = document.createElement("li")
      linha.textContent = `Check-in #${falha.checkin_id}: ${falha.mensagem}`
      this.falhasTarget.append(linha)
    }
    this.falhasTarget.hidden = !this.falhasTarget.childElementCount
    if (dados.erro) this.mostrarErro(dados.erro)
    if (this.hasBotaoTarget) this.botaoTarget.disabled = this.bloqueado || dados.ativa
    // Também detecta uma importação iniciada em outra aba ou pelo agendador.
    this.agendar(dados.ativa ? 2000 : 15000)
  }

  agendar(intervalo = 5000) {
    clearTimeout(this.timer)
    if (!this.desconectado) this.timer = setTimeout(() => this.consultar(), intervalo)
  }

  limparErro() {
    this.erroTarget.hidden = true
    this.erroTarget.textContent = ""
  }

  mostrarErro(mensagem) {
    this.erroTarget.textContent = mensagem
    this.erroTarget.hidden = false
  }
}
